/**
 * WP10d / #46643 — searchable not-found recovery for unknown old object ids.
 *
 * Unknown `/agents/verification/:id` (and `/verification/:id`) hops land here
 * with `?q=` set to the id. The page is a recovery surface, not a blank miss:
 * it names the unknown id, mirrors `?q=` into search, and lists live hits.
 */

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useApi } from "@excellent/ui/api";
import { search, type SearchHit } from "@excellent/ui/api/contracts/search";
import { useVerificationEnvOptional } from "../env-provider";
import { openVerificationSearch } from "../search-palette";
import { buildSearchRows, shellSearchQuery, type SearchRow } from "../search-commands";
import { ActionButton } from "../ui";
import { PageFrame, PageLink, type VerificationPageProps } from "./page-frame";

export type NotFoundRecoveryPageProps = VerificationPageProps & {
  query: string;
  unknownId: string;
  onSearch: (q: string) => void;
};

export function NotFoundRecoveryPage({
  onNavigate,
  query,
  unknownId,
  onSearch,
}: NotFoundRecoveryPageProps) {
  const env = useVerificationEnvOptional();
  const [draft, setDraft] = useState(query);
  const q = query.trim();
  const args =
    q.length > 0
      ? { query: shellSearchQuery({ q, environment: env?.environment ?? null }) }
      : null;
  const { data, isLoading } = useApi(search.shellGet, args, { keepPreviousData: true });
  const hits: SearchHit[] = data?.hits ?? [];
  const rows = useMemo(() => buildSearchRows(q, hits), [q, hits]);
  const hitRows = rows.filter((row): row is Extract<SearchRow, { kind: "hit" }> => row.kind === "hit");

  useEffect(() => {
    setDraft(query);
  }, [query]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    onSearch(draft.trim());
  }

  // The title is the most prominent text on the page, so it stays free of internal
  // identifiers. The id itself is still shown below, where it explains the broken link.
  const title = "Object not found";

  return (
    <PageFrame
      page="not-found"
      eyebrow="Recovery"
      title={title}
      lede="This old link does not resolve to a run, finding, receipt, or evidence object. Search for it, or go back to Overview."
      actions={
        <ActionButton
          actionId="not-found.open-search"
          data-verification-not-found-open-search=""
          onClick={() => openVerificationSearch()}
        >
          Open search
        </ActionButton>
      }
    >
      <div className="vp-card" data-testid="verification-not-found-recovery" data-verification-not-found="recovery">
        <div className="vp-empty">
          <div className="vp-empty-title">Searchable not-found</div>
          <p className="vp-empty-body">
            {unknownId ? (
              <>
                Unknown id <code data-verification-not-found-id="">{unknownId}</code> did not
                map onto the verification IA. Results below use the same search as the
                shell palette.
              </>
            ) : (
              <>This path did not include a resolvable object id.</>
            )}
          </p>
          <form className="vp-search" onSubmit={onSubmit} data-verification-not-found-search="">
            <input
              type="search"
              value={draft}
              placeholder="Search workflows, findings, settings…"
              aria-label="Search for the missing object"
              data-verification-not-found-query=""
              onChange={(event) => setDraft(event.target.value)}
            />
            <ActionButton actionId="submit" type="submit" variant="primary">Search</ActionButton>
          </form>
          <div className="flex flex-wrap items-center gap-2">
            <PageLink onNavigate={onNavigate} to="">
              Go to Overview
            </PageLink>
          </div>
        </div>
      </div>
      {q ? (
        <div className="vp-card" data-verification-not-found-results="">
          <div className="vp-empty-title">Results for {q}</div>
          {isLoading && !data ? (
            <p className="vp-empty-body">Searching…</p>
          ) : hitRows.length === 0 ? (
            <p className="vp-empty-body">No matching verification objects.</p>
          ) : (
            <ul>
              {hitRows.map((row) => (
                <li key={row.key}>
                  <a
                    href={row.hit.href}
                    onClick={(event) => {
                      event.preventDefault();
                      onNavigate(row.hit.href);
                    }}
                  >
                    {row.hit.label}
                  </a>
                  {row.hit.hint ? <span> — {row.hit.hint}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </PageFrame>
  );
}
